'use client';

import { useEffect, useState } from 'react';
import {
  X,
  GitPullRequest,
  Lightning,
  ChartLineUp,
} from '@phosphor-icons/react';
import { AnimatePresence, LazyMotion, m, domAnimation } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useUserLogged } from '@/hooks/useUserLogged';
import { isOwner } from '@/lib/rbac';

const DISMISS_STORAGE_PREFIX = 'sidebar-promo-dismissed:';

function readDismissed(userUuid: string): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(
      `${DISMISS_STORAGE_PREFIX}${userUuid}`
    );
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((v) => typeof v === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
}

function writeDismissed(userUuid: string, ids: Set<string>) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      `${DISMISS_STORAGE_PREFIX}${userUuid}`,
      JSON.stringify(Array.from(ids))
    );
  } catch {
    // Storage may be full or unavailable; ignore — dismissal still works for this session.
  }
}

function SlackIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M3.40896 10.0893C3.40896 11.0056 2.66171 11.7539 1.74533 11.7539C0.828957 11.7539 0.0808316 11.0056 0.0808316 10.0893C0.0808316 9.17307 0.829206 8.42469 1.74546 8.42469H3.40908L3.40896 10.0893ZM4.24758 10.0893C4.24758 9.17307 4.99596 8.42469 5.91221 8.42469C6.82846 8.42469 7.57683 9.17294 7.57683 10.0893V14.2562C7.57683 15.1724 6.82858 15.9208 5.91221 15.9208C4.99596 15.9208 4.24758 15.1724 4.24758 14.2562V10.0893Z"
        fill="currentColor"
      />
      <path
        d="M5.91221 3.40793C4.99596 3.40793 4.24758 2.66068 4.24758 1.7443C4.24758 0.827927 4.99596 0.0798026 5.91221 0.0798026C6.82846 0.0798026 7.57683 0.828177 7.57683 1.74443V3.40805L5.91221 3.40793ZM5.91221 4.24655C6.82846 4.24655 7.57683 4.99493 7.57683 5.91118C7.57683 6.82743 6.82858 7.5758 5.91221 7.5758H1.74533C0.829081 7.5758 0.0808316 6.82755 0.0808316 5.91118C0.0808316 4.99493 0.829206 4.24655 1.74546 4.24655H5.91221Z"
        fill="currentColor"
      />
      <path
        d="M12.5906 5.91118C12.5906 4.99493 13.3379 4.24655 14.2542 4.24655C15.1706 4.24655 15.9189 4.99493 15.9189 5.91118C15.9189 6.82743 15.1705 7.5758 14.2542 7.5758H12.5906V5.91118ZM11.752 5.91118C11.752 6.82743 11.0036 7.5758 10.0874 7.5758C9.17111 7.5758 8.42274 6.82755 8.42274 5.91118V1.7443C8.42274 0.828052 9.17099 0.0798026 10.0874 0.0798026C11.0036 0.0798026 11.752 0.828177 11.752 1.74443V5.91118Z"
        fill="currentColor"
      />
      <path
        d="M10.0874 12.5926C11.0036 12.5926 11.752 13.3398 11.752 14.2562C11.752 15.1726 11.0036 15.9208 10.0874 15.9208C9.17111 15.9208 8.42274 15.1724 8.42274 14.2562V12.5926H10.0874ZM10.0874 11.7539C9.17111 11.7539 8.42274 11.0056 8.42274 10.0893C8.42274 9.17307 9.17099 8.42469 10.0874 8.42469H14.2542C15.1705 8.42469 15.9189 9.17294 15.9189 10.0893C15.9189 11.0056 15.1705 11.7539 14.2542 11.7539H10.0874Z"
        fill="currentColor"
      />
    </svg>
  );
}

interface PromoSlide {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  href?: string;
  // Owner-only: slide links into /connectors, so hide it for non-org-wide
  // viewers the same way every other Connectors entry point is hidden.
  requiresOrgWideAdmin?: boolean;
}

const SLIDES: PromoSlide[] = [
  {
    id: 'slack',
    icon: <SlackIcon className="size-4 text-[#72884E]" />,
    title: 'Ask Ellie anything in Slack',
    description: 'One place to ask. One clear answer. No digging, no delays.',
    href: '/connectors?appname=slack',
    requiresOrgWideAdmin: true,
  },
  {
    id: 'github',
    icon: (
      <GitPullRequest
        size={16}
        weight="fill"
        className="text-ds-brand-subtle"
      />
    ),
    title: 'Review PRs with Ellie',
    description: 'Catch issues early. Ship with confidence, every single PR.',
    href: '/code-review',
  },
  {
    id: 'incidents',
    icon: (
      <Lightning size={16} weight="fill" className="text-ds-warning-default" />
    ),
    title: 'Resolve incidents faster',
    description: 'Ellie surfaces context the moment something breaks.',
    href: '/incident-knowledge',
  },
  {
    id: 'insights',
    icon: (
      <ChartLineUp size={16} weight="fill" className="text-ds-brand-subtle" />
    ),
    title: 'See what your team ships',
    description: 'Real engineering insights, no spreadsheets, no guessing.',
    href: '/team-insight',
  },
];

const ROTATE_MS = 6000;

interface SidebarPromoCardProps {
  isCollapsed: boolean;
  /** Notify parent when card is dismissed so it can shrink reserved scroll space. */
  onDismissChange?: (dismissed: boolean) => void;
}

export function SidebarPromoCard({
  isCollapsed,
  onDismissChange,
}: SidebarPromoCardProps) {
  const { user } = useUserLogged();
  const userUuid = user?.UserUUID ?? null;

  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [paused, setPaused] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!userUuid) {
      setHydrated(false);
      return;
    }
    setDismissedIds(readDismissed(userUuid));
    setHydrated(true);
  }, [userUuid]);

  const orgWide = isOwner(user?.Role);
  const visibleSlides = SLIDES.filter(
    (s) => !dismissedIds.has(s.id) && (!s.requiresOrgWideAdmin || orgWide)
  );
  const allDismissed = visibleSlides.length === 0;

  useEffect(() => {
    if (paused || allDismissed || isCollapsed) return;
    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % visibleSlides.length);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [paused, allDismissed, isCollapsed, visibleSlides.length]);

  const isHidden = !hydrated || !userUuid || allDismissed;
  // Notify the parent only about *actual* dismissal state (allDismissed),
  // not the SSR/hydration gate (`isHidden` is true while hydrated=false).
  // Otherwise the parent flips its padding on every mount and snaps back
  // once localStorage hydrates, producing a visible layout shift.
  useEffect(() => {
    onDismissChange?.(allDismissed);
  }, [allDismissed, onDismissChange]);

  if (isHidden || isCollapsed) return null;

  // Clamp activeIndex into the visible range so a dismissal that shrinks the
  // list past the current pointer doesn't crash the render.
  const safeIndex = Math.min(activeIndex, visibleSlides.length - 1);
  const slide = visibleSlides[safeIndex];

  // Build the visible stack: front card + up to 2 cards behind it. As slides
  // get dismissed the stack shrinks (3 → 2 → 1 → 0). Each entry carries its
  // slot index (0 = front, 1 = mid, 2 = back) which drives its transform.
  const STACK_DEPTH = 3;
  const stackEntries: Array<{ slide: PromoSlide; slot: number }> = [];
  for (let i = 0; i < Math.min(visibleSlides.length, STACK_DEPTH); i++) {
    stackEntries.push({
      slide: visibleSlides[(safeIndex + i) % visibleSlides.length],
      slot: i,
    });
  }

  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(slide.id);
      if (userUuid) writeDismissed(userUuid, next);
      return next;
    });
    // After removing the current slide, the same numeric index now points at
    // the slide that came after it. Wrap when the dismissed one was last.
    setActiveIndex((prev) => {
      const remaining = visibleSlides.length - 1;
      if (remaining <= 0) return 0;
      return prev % remaining;
    });
  };

  const handleDotClick = (idx: number) => {
    setActiveIndex(idx);
    setPaused(true);
  };

  return (
    <LazyMotion features={domAnimation}>
      <div className="relative pt-8">
        {/* Invisible sizer — renders the current front-card content in normal
            flow so the wrapper grows to fit it. The animated cards below are
            all absolutely positioned and would otherwise leave the wrapper
            with zero intrinsic height, clipping the visible card. */}
        <div className="invisible pointer-events-none" aria-hidden="true">
          <CardBody
            slide={slide}
            isFront
            onDismiss={handleDismiss}
            paused={paused}
            onPauseHover={setPaused}
            visibleSlides={visibleSlides}
            safeIndex={safeIndex}
            onDotClick={handleDotClick}
          />
        </div>

        {/* Stack rendered as full-size layered cards. Each layer's slot index
            (0 = front, 1 = mid, 2 = back) drives a transform — scale shrinks,
            y lifts up, opacity drops. When activeIndex changes each layer's
            slot changes too; framer animates the transform diff and the deck
            reads as physically rotating forward. */}
        <AnimatePresence initial={false}>
          {stackEntries.map(({ slide: entrySlide, slot }) => {
            const isFront = slot === 0;
            return (
              <m.div
                key={entrySlide.id}
                initial={{ opacity: 0, scale: 0.8, y: -44 }}
                animate={{
                  opacity: slot === 0 ? 1 : slot === 1 ? 0.55 : 0.28,
                  scale: 1 - slot * 0.07,
                  y: -slot * 16,
                }}
                exit={{ opacity: 0, scale: 0.92, y: 44, rotate: -2.5 }}
                transition={{
                  duration: 0.52,
                  ease: [0.22, 1, 0.36, 1],
                  delay: slot * 0.04,
                }}
                className="absolute inset-x-0 top-8"
                style={{
                  zIndex: 30 - slot * 10,
                  transformOrigin: 'top center',
                  pointerEvents: isFront ? 'auto' : 'none',
                  filter: isFront ? undefined : `blur(${slot * 0.4}px)`,
                }}
                aria-hidden={!isFront}
              >
                <CardBody
                  slide={entrySlide}
                  isFront={isFront}
                  onDismiss={handleDismiss}
                  paused={paused}
                  onPauseHover={setPaused}
                  visibleSlides={visibleSlides}
                  safeIndex={safeIndex}
                  onDotClick={handleDotClick}
                />
              </m.div>
            );
          })}
        </AnimatePresence>
      </div>
    </LazyMotion>
  );
}

function CardBody({
  slide,
  isFront,
  onDismiss,
  paused,
  onPauseHover,
  visibleSlides,
  safeIndex,
  onDotClick,
}: {
  slide: PromoSlide;
  isFront: boolean;
  onDismiss: (e: React.MouseEvent) => void;
  paused: boolean;
  onPauseHover: (paused: boolean) => void;
  visibleSlides: PromoSlide[];
  safeIndex: number;
  onDotClick: (idx: number) => void;
}) {
  const baseClass = cn(
    'relative block rounded-lg border border-dashed border-ds-border-subtlest bg-ds-surface-white p-2.5',
    isFront && 'hover:bg-ds-brand-lightest transition-colors duration-100'
  );
  const inner = (
    <>
      {isFront ? (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="absolute top-2 right-2 flex items-center justify-center size-4 rounded text-ds-text-subtler hover:text-ds-text-darker hover:bg-ds-gray-subtlest transition-colors"
        >
          <X size={12} weight="bold" />
        </button>
      ) : null}

      <div className="flex flex-col gap-2.5">
        <div className="flex size-[26px] items-center justify-center rounded-lg border border-ds-brand-subtlest bg-gradient-to-b from-ds-brand-lightest to-ds-brand-subtlest">
          {slide.icon}
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="pr-5 font-semibold text-[12px] leading-[1.3] text-ds-brand-subtle">
            {slide.title}
          </h3>
          <p className="font-medium text-[12px] leading-[1.3] text-ds-text-subtle">
            {slide.description}
          </p>
        </div>
      </div>

      {isFront ? (
        <div className="mt-4 flex items-center justify-start gap-2">
          {visibleSlides.map((s, idx) => {
            const isActiveDot = idx === safeIndex;
            return (
              <button
                key={s.id}
                type="button"
                aria-label={`Show slide ${idx + 1}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDotClick(idx);
                }}
                className={cn(
                  'relative h-1.5 rounded-full overflow-hidden transition-[width,background-color] duration-300 ease-out',
                  isActiveDot
                    ? 'w-6 bg-ds-brand-subtlest'
                    : 'w-1.5 bg-ds-brand-subtlest hover:bg-ds-brand-subtler/60'
                )}
              >
                {isActiveDot ? (
                  <span
                    key={`${slide.id}-${safeIndex}`}
                    aria-hidden="true"
                    className="absolute inset-0 origin-left rounded-full bg-ds-brand-subtler"
                    style={{
                      animation: `sidebar-promo-progress ${ROTATE_MS}ms linear forwards`,
                      animationPlayState: paused ? 'paused' : 'running',
                    }}
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : (
        // Spacer so the ghost card occupies the same height as the front
        // card (keeps the stack's bottom edge aligned across all layers).
        <div className="mt-4 h-1.5" />
      )}
    </>
  );

  if (!isFront) {
    return <div className={baseClass}>{inner}</div>;
  }
  return (
    <a
      href={slide.href ?? '#'}
      onMouseEnter={() => onPauseHover(true)}
      onMouseLeave={() => onPauseHover(false)}
      className={baseClass}
    >
      {inner}
    </a>
  );
}
